import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputHelperTextComponent } from 'app/shared/components/input-helper-text/input-helper-text.component';
import { StorageContainersEnum } from 'app/shared/enums/blob-storage.enum';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { BlobStorageService } from 'app/shared/services/blob-storage.service';
import { HelperService } from 'app/shared/services/helper.service';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';

@Component({
  selector: 'app-upload-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    HttpClientModule,
    NgxFileDropModule,
    InputHelperTextComponent
  ],
  templateUrl: './upload-attachments.component.html',
  styleUrl: './upload-attachments.component.scss'
})
export class UploadAttachmentsComponent {
  @Input() uploadedFiles: { name: string; blobStorageId: string; type: string; size: string }[] = [];
  @Input() touched!: boolean;
  @Input() disabled!: boolean;
  @Input() caseId!: number;
  @Input() caseType!: string;
  @Input() isRequired!: boolean;
  @Input() readonly!: boolean;
  @Input() label!: string;
  @Input() allowMultiple: boolean = true;
  @Input() helpText!: string;
  @Output() attachmentsChange = new EventEmitter<
    { name: string; blobStorageId: string; type: string; size: string }[]
  >();
  public files: NgxFileDropEntry[] = [];
  fileSizeLimitInMB: number = environment.appConfig.attachmentsFileSizeLimitInMB;

  constructor(private blobStorageService: BlobStorageService, private toastr: ToastrService, private translate: TranslateService, private helperService: HelperService) {}

  ngOnChanges(): void {
    this.touched = false;
  }

  public dropped(files: NgxFileDropEntry[]): void {
    if (!this.allowMultiple && files.length > 1) {
      const errorMessage = this.translate.instant('messages.document_upload_one_error');
      this.toastr.show(errorMessage, ToastTypesEnum.ERROR);
      return;
    }

    const selectedFiles = this.allowMultiple ? files : files.slice(0, 1);

    selectedFiles.forEach((droppedFile) => {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file((file: File) => {
          const fileSizeInKB = Math.round(file.size / 1024);
          const fileSizeInKBString = fileSizeInKB + ' KB';

          if(fileSizeInKB < (1024 * environment.appConfig.attachmentsFileSizeLimitInMB)) {
            this.blobStorageService.uploadFile(file, StorageContainersEnum.ATTACHMENT).subscribe((response) => {
              const fileMeta = {
                name: file.name,
                blobStorageId: response.blobStorageId,
                type: file.type,
                size: fileSizeInKBString,
              };

              this.uploadedFiles.push(fileMeta);
              this.touched = true;
              this.attachmentsChange.emit(this.uploadedFiles);
            });
          } else {
            this.toastr.show(this.translate.instant('messages.file_size_error_message', {fileName: file.name, sizeLimit: this.fileSizeLimitInMB}), ToastTypesEnum.ERROR);
          }
        });
      }
    });
  }

  downloadFile(file: any) {
    const fileId = file?.storageFileId || file?.blobStorageId;
    if (!fileId) return;
    this.blobStorageService.downloadFile(fileId).subscribe((blob) => {
      this.helperService.saveFile(file, blob);
    });
  }

  downloadAllAttachments() {
    const file = { fileName: `${this.caseType}-${this.caseId}.zip` };
    this.blobStorageService.downloadAllAttachments(this.caseType, this.caseId).pipe(take(1)).subscribe(blob => {
      this.helperService.saveFile(file, blob);
    });
  }

  public removeFile(file: { name: string; blobStorageId: string; type: string; size: string }): void {
    this.blobStorageService.deleteFile(file.blobStorageId).subscribe(() => {
      this.uploadedFiles = this.uploadedFiles.filter((f) => f.blobStorageId !== file.blobStorageId);
      this.touched = true;
      this.attachmentsChange.emit(this.uploadedFiles);
    });
  }

  public fileOver(event: any){
    console.log(event);
  }

  public fileLeave(event: any){
    console.log(event);
  }

}
